import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Container,
  Form,
  Modal,
  Row,
  Spinner,
  Tab,
  Tabs,
} from "react-bootstrap";
import Cropper from "react-easy-crop";
import "react-easy-crop/react-easy-crop.css";
import { useNavigate } from "react-router-dom";
import {
  FaArrowDown,
  FaArrowLeft,
  FaArrowUp,
  FaCloudDownloadAlt,
  FaCompress,
  FaCode,
  FaCrop,
  FaExpand,
  FaEye,
  FaUpload,
} from "react-icons/fa";
import { auth, fires } from "../../firebase";
import firebase from "firebase";

const MAILERSEND_API_BASE = process.env.REACT_APP_MAILERSEND_API_BASE || "/mailersend-api";
const WEEKLY_TEMPLATE_ID = "3vz9dleyqn74kj50";
const EVENT_AREA_MARKER = "<!-- Event Area -->";
const SECONDARY_CTA_MARKER = "<!-- ============ Secondary CTA ============ -->";
const FALLBACK_EVENT_IMAGE = "https://eventgoimg.com/eventgo_white_bg.png";
const CAMPAIGN_TEMPLATE_PATH = `${process.env.PUBLIC_URL || ""}/emails/campaign.html`;
const IMAGE_PROXY_PATH = "/image-proxy";
const IMAGE_UPLOAD_API_URL = "https://v1serviceapiuploadfile-650318705789.us-central1.run.app";
const DEFAULT_IMAGE_ZOOM = 100;
const MIN_IMAGE_ZOOM = 100;
const MAX_IMAGE_ZOOM = 240;
const EMAIL_CARD_IMAGE_WIDTH = 170;
const EMAIL_CARD_IMAGE_HEIGHT = 170;
const EMAIL_CARD_IMAGE_COLUMN_WIDTH = 194;
const CROP_IMAGE_WIDTH = 900;
const CROP_IMAGE_HEIGHT = 900;
const CROP_IMAGE_ASPECT = 1;
const CROP_PREVIEW_WIDTH = 500;
const CROP_PREVIEW_HEIGHT = 500;

const getDefaultEventImageCrop = () => ({
  width: EMAIL_CARD_IMAGE_WIDTH,
  height: EMAIL_CARD_IMAGE_HEIGHT,
  fit: "cover",
  fillFrame: false,
  zoom: DEFAULT_IMAGE_ZOOM,
  positionX: 50,
  positionY: 50,
});

const clampNumber = (value, min, max, fallback) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return fallback;
  return Math.min(max, Math.max(min, numericValue));
};

const getEventImageSrc = (event) =>
  event.photoURL ||
  event.image ||
  event.photo ||
  event.imageURL ||
  event.images?.[0] ||
  event.imageURLs?.[0] ||
  FALLBACK_EVENT_IMAGE;

const getEditableImageSrc = (imageSrc) => {
  if (!imageSrc || imageSrc.startsWith("data:") || imageSrc.startsWith("blob:")) {
    return imageSrc;
  }

  try {
    const parsedUrl = new URL(imageSrc, window.location.href);
    if (parsedUrl.origin === window.location.origin) {
      return parsedUrl.href;
    }
  } catch (err) {
    return imageSrc;
  }

  return `${IMAGE_PROXY_PATH}?url=${encodeURIComponent(imageSrc)}`;
};

const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Could not load image for editing."));
    image.src = url;
  });

const createCroppedImageBlob = async (imageSrc, pixelCrop) => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  canvas.width = CROP_IMAGE_WIDTH;
  canvas.height = CROP_IMAGE_HEIGHT;
  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    CROP_IMAGE_WIDTH,
    CROP_IMAGE_HEIGHT
  );

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error("Could not create cropped image."));
        }
      },
      "image/jpeg",
      0.92
    );
  });
};

const getCenteredCropPixels = (mediaSize) => {
  const mediaWidth = mediaSize?.naturalWidth || mediaSize?.width || 0;
  const mediaHeight = mediaSize?.naturalHeight || mediaSize?.height || 0;
  if (!mediaWidth || !mediaHeight) return null;

  let width = mediaWidth;
  let height = Math.round(width / CROP_IMAGE_ASPECT);
  if (height > mediaHeight) {
    height = mediaHeight;
    width = Math.round(height * CROP_IMAGE_ASPECT);
  }

  return {
    x: Math.max(0, Math.round((mediaWidth - width) / 2)),
    y: Math.max(0, Math.round((mediaHeight - height) / 2)),
    width: Math.round(width),
    height: Math.round(height),
  };
};

export default function AdminMailerSendTemplateEditor() {
  const navigate = useNavigate();

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [templateName, setTemplateName] = useState("");
  const [htmlContent, setHtmlContent] = useState("");
  const [textContent, setTextContent] = useState("");
  const [mobilePreviewWidth, setMobilePreviewWidth] = useState(390);
  const [templateMeta, setTemplateMeta] = useState(null);
  const [fetching, setFetching] = useState(false);
  const [updatingMailerSend, setUpdatingMailerSend] = useState(false);
  const [featuredEvents, setFeaturedEvents] = useState([]);
  const [selectedEventIds, setSelectedEventIds] = useState([]);
  const [eventImageCrops, setEventImageCrops] = useState({});
  const [isEventEditorFullScreen, setIsEventEditorFullScreen] = useState(false);
  const [loadingFeaturedEvents, setLoadingFeaturedEvents] = useState(false);
  const [showImageEditorModal, setShowImageEditorModal] = useState(false);
  const [editingEventId, setEditingEventId] = useState("");
  const [editorImageSrc, setEditorImageSrc] = useState("");
  const [editorExportImageSrc, setEditorExportImageSrc] = useState("");
  const [editorSelectedFile, setEditorSelectedFile] = useState(null);
  const [editorCrop, setEditorCrop] = useState({ x: 0, y: 0 });
  const [editorZoom, setEditorZoom] = useState(1);
  const [editorCroppedAreaPixels, setEditorCroppedAreaPixels] = useState(null);
  const [uploadingEditedImage, setUploadingEditedImage] = useState(false);
  const [imageEditorError, setImageEditorError] = useState("");

  useEffect(() => {
    loadCampaignHtmlTemplate();
    loadFeaturedEvents();
  }, []);

  const getTemplatePayload = (data) => data?.data || data?.template || data || {};

  const getTemplateHtml = (template) =>
    template.html ||
    template.htmlContent ||
    template.html_content ||
    template.content ||
    template.body ||
    "";

  const getTemplateText = (template) =>
    template.text || template.textContent || template.text_content || "";

  const usesLocalDevMailerSendProxy = () =>
    process.env.NODE_ENV === "development" && MAILERSEND_API_BASE === "/mailersend-api";

  const getLocalMailerSendToken = () =>
    process.env.REACT_APP_MS_TOKEN ||
    "";

  const getMailerSendAuthHeaders = async () => {
    if (usesLocalDevMailerSendProxy()) {
      const token = getLocalMailerSendToken();
      if (!token) {
        throw new Error("Missing REACT_APP_MS_TOKEN for local development proxy.");
      }

      return {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      };
    }

    const idToken = await auth.currentUser?.getIdToken?.();
    if (!idToken) {
      throw new Error("Please sign in again before using the MailerSend template editor.");
    }

    return {
      Authorization: `Bearer ${idToken}`,
      "Content-Type": "application/json",
    };
  };

  const parseMailerSendResponse = async (response) => {
    const responseText = await response.text();
    const contentType = response.headers.get("content-type") || "";

    if (contentType.includes("application/json")) {
      return responseText ? JSON.parse(responseText) : null;
    }

    if (responseText.trim().startsWith("<!doctype") || responseText.trim().startsWith("<html")) {
      throw new Error(
        "MailerSend proxy returned HTML instead of JSON. Deploy the Firebase mailersendApi function and hosting rewrite before using this in production."
      );
    }

    try {
      return responseText ? JSON.parse(responseText) : null;
    } catch (err) {
      throw new Error("MailerSend returned a non-JSON response.");
    }
  };

  const loadCampaignHtmlTemplate = async () => {
    try {
      setFetching(true);
      setError("");

      const response = await fetch(`${CAMPAIGN_TEMPLATE_PATH}?t=${Date.now()}`, {
        cache: "no-store",
      });
      if (!response.ok) {
        throw new Error(`Campaign HTML request failed (${response.status})`);
      }

      const templateHtml = await response.text();
      setHtmlContent(templateHtml);
      setTemplateName((current) => current || "Weekly Email Campaign");
    } catch (err) {
      setError("Failed to load campaign.html: " + err.message);
    } finally {
      setFetching(false);
    }
  };

  const escapeHtml = (value) =>
    String(value || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");

  const getEventUrl = (event) =>
    `https://www.eventgo.ai/event/${event.id}?utm_source=campaign&source=campaign&campaignId={{campaignId}}&email={{email}}`;

  const getEventDescription = (event) =>
    event.description ||
    event.subtitle ||
    event.location ||
    "Live upcoming event on EventGo";

  const getEventLocation = (event) => {
    if (typeof event.address === "string") return event.address;
    return event.address?.fullAddress || event.location || "";
  };

  const formatEventDate = (event) => {
    const rawDate = event.startDate;
    const date = rawDate?.toDate?.() ||
      (rawDate?.toMillis ? new Date(rawDate.toMillis()) : null) ||
      (rawDate instanceof Date ? rawDate : null);

    if (!date) return event.startDateString || "";

    return date.toLocaleString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const normalizeEvent = (doc) => {
    const data = doc.data();

    return {
      id: doc.id,
      ...data,
      title: data.title || data.name || "Untitled event",
      photoURL: data.photoURL || data.image || data.photo || "",
      description: data.description || data.shortDescription || data.subtitle || "",
      location: data.address?.fullAddress || data.location || "",
      startDateString: data.startDateString || "",
      websiteURL: data.websiteURL || data.website || data.eventURL || "",
    };
  };

  const loadFeaturedEvents = async () => {
    try {
      setLoadingFeaturedEvents(true);
      const now = firebase.firestore.Timestamp.now();
      let events = [];

      try {
        const eventsSnapshot = await fires
          .collection("events")
          .where("status", "==", "live")
          .where("approved", "==", true)
          .where("startDate", ">", now)
          .orderBy("startDate", "asc")
          .limit(150)
          .get();

        events = eventsSnapshot.docs.map(normalizeEvent);
      } catch (queryErr) {
        const fallbackSnapshot = await fires
          .collection("events")
          .where("status", "==", "live")
          .where("approved", "==", true)
          .limit(150)
          .get();

        events = fallbackSnapshot.docs
          .map(normalizeEvent)
          .filter((event) => {
            const startDate = event.startDate?.toDate?.() ||
              (event.startDate?.toMillis ? new Date(event.startDate.toMillis()) : null);

            return startDate && startDate > new Date();
          })
          .sort((a, b) => {
            const aTime = a.startDate?.toMillis?.() || a.startDate?.toDate?.()?.getTime?.() || 0;
            const bTime = b.startDate?.toMillis?.() || b.startDate?.toDate?.()?.getTime?.() || 0;
            return aTime - bTime;
          });
      }

      setFeaturedEvents(events);
      setSelectedEventIds((current) =>
        current.filter((eventId) => events.some((event) => event.id === eventId))
      );
    } catch (err) {
      setError("Failed to load live upcoming events: " + err.message);
    } finally {
      setLoadingFeaturedEvents(false);
    }
  };

  const toggleSelectedEvent = (eventId) => {
    setSelectedEventIds((current) => {
      if (current.includes(eventId)) {
        return current.filter((id) => id !== eventId);
      }

      setEventImageCrops((crops) => ({
        ...crops,
        [eventId]: crops[eventId] || getDefaultEventImageCrop(),
      }));

      return [...current, eventId];
    });
  };

  const getEventImageCrop = (eventId) => {
    const crop = eventImageCrops[eventId] || {};

    return {
      width: CROP_IMAGE_WIDTH,
      height: CROP_IMAGE_HEIGHT,
      fit: crop.fit === "contain" ? "contain" : "cover",
      fillFrame: Boolean(crop.fillFrame),
      zoom: clampNumber(crop.zoom, MIN_IMAGE_ZOOM, MAX_IMAGE_ZOOM, DEFAULT_IMAGE_ZOOM),
      positionX: clampNumber(crop.positionX, 0, 100, 50),
      positionY: clampNumber(crop.positionY, 0, 100, 50),
    };
  };

  const updateEventImageCrop = (eventId, cropUpdates) => {
    setEventImageCrops((crops) => ({
      ...crops,
      [eventId]: {
        ...getEventImageCrop(eventId),
        ...cropUpdates,
      },
    }));
  };

  const handleFillImagePointer = (eventId, event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const positionX = Math.min(
      100,
      Math.max(0, ((event.clientX - rect.left) / rect.width) * 100)
    );
    const positionY = Math.min(
      100,
      Math.max(0, ((event.clientY - rect.top) / rect.height) * 100)
    );

    updateEventImageCrop(eventId, {
      fit: "cover",
      fillFrame: true,
      positionX: Math.round(positionX),
      positionY: Math.round(positionY),
    });
  };

  const applyFullImageCrop = (event) => {
    updateEventImageCrop(event.id, {
      fit: "contain",
      fillFrame: false,
      width: CROP_IMAGE_WIDTH,
      height: CROP_IMAGE_HEIGHT,
      zoom: DEFAULT_IMAGE_ZOOM,
      positionX: 50,
      positionY: 50,
    });
  };

  const applyFillImageCrop = (event) => {
    const crop = getEventImageCrop(event.id);

    updateEventImageCrop(event.id, {
      fit: "cover",
      fillFrame: true,
      width: CROP_IMAGE_WIDTH,
      height: CROP_IMAGE_HEIGHT,
      zoom: crop.zoom,
      positionX: 50,
      positionY: 50,
    });
  };

  const openImageEditor = (event) => {
    const imageSrc = getEventImageSrc(event);
    setEditingEventId(event.id);
    setEditorImageSrc(imageSrc);
    setEditorExportImageSrc(getEditableImageSrc(imageSrc));
    setEditorSelectedFile(null);
    setEditorCrop({ x: 0, y: 0 });
    setEditorZoom(1);
    setEditorCroppedAreaPixels(null);
    setImageEditorError("");
    setShowImageEditorModal(true);
  };

  const closeImageEditor = (forceClose = false) => {
    if (uploadingEditedImage && !forceClose) return;
    setShowImageEditorModal(false);
    setEditingEventId("");
    setEditorImageSrc("");
    setEditorExportImageSrc("");
    setEditorSelectedFile(null);
    setEditorCrop({ x: 0, y: 0 });
    setEditorZoom(1);
    setEditorCroppedAreaPixels(null);
    setImageEditorError("");
  };

  const handleEditorImageFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setEditorSelectedFile(file);
    const reader = new FileReader();
    reader.onload = () => {
      setEditorImageSrc(reader.result);
      setEditorExportImageSrc(reader.result);
      setEditorCrop({ x: 0, y: 0 });
      setEditorZoom(1);
      setEditorCroppedAreaPixels(null);
      setImageEditorError("");
    };
    reader.onerror = () => setImageEditorError("Could not read the selected image file.");
    reader.readAsDataURL(file);
    event.target.value = "";
  };

  const handleEditorImageLoaded = (mediaSize) => {
    const centeredCrop = getCenteredCropPixels(mediaSize);
    if (centeredCrop) {
      setEditorCroppedAreaPixels(centeredCrop);
    }
  };

  const uploadImageFile = async (file) => {
    const response = await fetch(IMAGE_UPLOAD_API_URL, {
      method: "POST",
      headers: {
        "Access-Control-Allow-Origin": "*",
        "Content-Type": file.type || "application/octet-stream",
      },
      body: file,
    });

    if (!response.ok) {
      throw new Error(`Image upload failed (${response.status})`);
    }

    const data = await response.json();
    if (!data.success || !data.downloadURL) {
      throw new Error(data.message || "Image upload did not return a download URL.");
    }

    return data.downloadURL;
  };

  const uploadEditedEventImage = async (blob, eventId) => {
    const file = new File([blob], `mailersend-event-${eventId}-${Date.now()}.jpg`, {
      type: "image/jpeg",
    });
    return uploadImageFile(file);
  };

  const downloadEventImage = async (event) => {
    const imageSrc = getEventImageSrc(event);
    if (!imageSrc) return;

    const fileName = `${(event.title || "event-image")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "") || "event-image"}.jpg`;

    try {
      const response = await fetch(getEditableImageSrc(imageSrc));
      if (!response.ok) {
        throw new Error(`Image download failed (${response.status})`);
      }

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      window.open(imageSrc, "_blank", "noopener,noreferrer");
    }
  };

  const applyUploadedEventImage = (eventId, previousImageUrl, downloadURL) => {
    setFeaturedEvents((events) =>
      events.map((event) =>
        event.id === eventId
          ? {
              ...event,
              photoURL: downloadURL,
              image: downloadURL,
            }
          : event
      )
    );
    if (previousImageUrl) {
      setHtmlContent((current) =>
        current
          .split(previousImageUrl)
          .join(downloadURL)
          .split(escapeHtml(previousImageUrl))
          .join(escapeHtml(downloadURL))
      );
    }
    updateEventImageCrop(eventId, {
      fit: "cover",
      fillFrame: false,
      width: CROP_IMAGE_WIDTH,
      height: CROP_IMAGE_HEIGHT,
      zoom: DEFAULT_IMAGE_ZOOM,
      positionX: 50,
      positionY: 50,
    });
  };

  const saveEditedEventImage = async () => {
    if (!editingEventId || !editorImageSrc) {
      setImageEditorError("Please wait for the crop editor to finish loading before saving.");
      return;
    }

    try {
      setUploadingEditedImage(true);
      setError("");
      setImageEditorError("");
      const previousImageUrl = editingEvent ? getEventImageSrc(editingEvent) : "";
      const exportImageSrc = editorExportImageSrc || editorImageSrc;
      const cropPixels =
        editorCroppedAreaPixels ||
        getCenteredCropPixels(await createImage(exportImageSrc));
      if (!cropPixels) {
        throw new Error("Could not read the original image crop area.");
      }

      let croppedBlob;
      try {
        croppedBlob = await createCroppedImageBlob(exportImageSrc, cropPixels);
      } catch (cropErr) {
        if (exportImageSrc === editorImageSrc) {
          throw cropErr;
        }
        croppedBlob = await createCroppedImageBlob(editorImageSrc, cropPixels);
      }
      const downloadURL = await uploadEditedEventImage(croppedBlob, editingEventId);

      applyUploadedEventImage(editingEventId, previousImageUrl, downloadURL);
      setSuccess("Edited event image uploaded and applied.");
      closeImageEditor(true);
    } catch (err) {
      const errorMessage = err.message || "Unknown error";
      setImageEditorError(
        errorMessage.includes("tainted") ||
          errorMessage.includes("cross-origin") ||
          errorMessage.includes("Could not load image")
          ? "This remote image could not be prepared for cropping. Make sure the image proxy function is deployed, or choose the image file with the source image picker."
          : "Failed to save edited image: " + errorMessage
      );
    } finally {
      setUploadingEditedImage(false);
    }
  };

  const uploadSelectedImageWithoutEditing = async () => {
    if (!editingEventId || !editorSelectedFile) {
      setImageEditorError("Choose a new source image before uploading without editing.");
      return;
    }

    try {
      setUploadingEditedImage(true);
      setError("");
      setImageEditorError("");
      const previousImageUrl = editingEvent ? getEventImageSrc(editingEvent) : "";
      const downloadURL = await uploadImageFile(editorSelectedFile);

      applyUploadedEventImage(editingEventId, previousImageUrl, downloadURL);
      setSuccess("New event image uploaded and applied.");
      closeImageEditor(true);
    } catch (err) {
      setImageEditorError("Failed to upload selected image: " + (err.message || "Unknown error"));
    } finally {
      setUploadingEditedImage(false);
    }
  };

  const moveSelectedEvent = (eventId, direction) => {
    setSelectedEventIds((current) => {
      const currentIndex = current.indexOf(eventId);
      const nextIndex = currentIndex + direction;

      if (
        currentIndex === -1 ||
        nextIndex < 0 ||
        nextIndex >= current.length
      ) {
        return current;
      }

      const next = [...current];
      const [movedEventId] = next.splice(currentIndex, 1);
      next.splice(nextIndex, 0, movedEventId);
      return next;
    });
  };

  const getSelectedEvents = () =>
    selectedEventIds
      .map((eventId) => featuredEvents.find((event) => event.id === eventId))
      .filter(Boolean);

  const buildEventCardHtml = (event, index) => {
    const eventName = escapeHtml(event.title || "Untitled event");
    const eventLocation = escapeHtml(getEventLocation(event) || "Location TBA");
    const eventDate = escapeHtml(formatEventDate(event) || event.startDateString || "Date TBA");
    const eventImageUrl = escapeHtml(getEventImageSrc(event));
    const eventLink = escapeHtml(getEventUrl(event));
    const imageWidth = EMAIL_CARD_IMAGE_WIDTH;
    const spacer = index === 0 ? "" : '\n      <tr><td style="height:14px;line-height:14px;font-size:0;">&nbsp;</td></tr>\n';

    return `${spacer}      <tr><td class="px" style="padding:0 10px 14px 10px;">
        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background:#FFFFFF;border-radius:28px;overflow:hidden;">
          <tr>
            <td class="event-card-image-cell" width="${EMAIL_CARD_IMAGE_COLUMN_WIDTH}" valign="top" align="center" style="width:${EMAIL_CARD_IMAGE_COLUMN_WIDTH}px;padding:12px;background:#FFFFFF;line-height:0;">
              <div class="event-card-image-frame" style="width:${imageWidth}px;height:${EMAIL_CARD_IMAGE_HEIGHT}px;aspect-ratio:1 / 1;overflow:hidden;border-radius:18px;">
                <a builder-link-id="7066" href="${eventLink}" style="text-decoration:none;display:block;width:100%;height:100%;">
                  <img class="card-img" src="${eventImageUrl}" alt="" width="${imageWidth}" height="${EMAIL_CARD_IMAGE_HEIGHT}" style="display:block;width:100%;height:100%;aspect-ratio:1 / 1;object-fit:cover;border:0;border-radius:18px;outline:none;text-decoration:none;">
                </a>
              </div>
            </td>
            <td class="event-card-content-cell" valign="middle" style="padding:12px 22px 12px 0;">
              <a builder-link-id="7067" href="${eventLink}" style="text-decoration:none;color:#0B0C0A;">
                <div class="event-card-title" style="font-family:'Noto Sans',Arial,sans-serif;font-weight:800;font-size:22px;line-height:1.18;letter-spacing:-0.025em;color:#0B0C0A;">${eventName}</div>
              </a>
              <table class="event-card-meta" role="presentation" cellpadding="0" cellspacing="0" border="0" style="font-family:'Noto Sans',Arial,sans-serif;font-size:13px;color:#52524A;line-height:1.5;margin-top:14px;">
                <tr>
                  <td class="event-card-meta-icon" width="22" valign="top" style="width:22px;padding:0 6px 0 0;white-space:nowrap;">🗓</td>
                  <td valign="top" style="padding:0;">${eventDate}</td>
                </tr>
                <tr>
                  <td class="event-card-meta-icon" width="22" valign="top" style="width:22px;padding:4px 6px 0 0;white-space:nowrap;">📍</td>
                  <td valign="top" style="padding:4px 0 0 0;">${eventLocation}</td>
                </tr>
              </table>
              <div class="event-card-action" style="margin-top:20px;">
                <a builder-link-id="7068" href="${eventLink}" style="display:inline-block;background:#0B0C0A;color:#FFFFFF;font-family:'Noto Sans',Arial,sans-serif;font-size:12px;font-weight:800;line-height:1;text-decoration:none;padding:11px 16px;border-radius:999px;">View event</a>
              </div>
            </td>
          </tr>
        </table>
      </td></tr>`;
  };

  const buildSelectedEventCardsHtml = () => {
    return getSelectedEvents().map(buildEventCardHtml).join("\n");
  };

  const normalizeResponsiveEventCardsHtml = (html) =>
    html
      .replace(
        /<div(?: class="event-image-frame")? style="width:580px;min-width:580px;max-width:580px;height:326px;min-height:326px;max-height:326px;overflow:hidden;background:#F4F4F0;margin:0 auto;line-height:0;">/g,
        '<div class="event-image-frame" style="width:100%;max-width:580px;overflow:hidden;background:#F4F4F0;margin:0 auto;line-height:0;">'
      )
      .replace(
        /(<img class="card-img"[^>]*?) width="580" height="326" style="display:block;width:580px;min-width:580px;max-width:580px;height:326px;min-height:326px;max-height:326px;border:0;outline:none;text-decoration:none;[^"]*">/g,
        '$1 width="580" style="display:block;width:100%;max-width:580px;height:auto;border:0;outline:none;text-decoration:none;">'
      );

  const insertSelectedEvents = async () => {
    if (selectedEventIds.length === 0) {
      setError("Please select at least one featured event.");
      return;
    }

    const currentHtml = htmlContent;
    if (!currentHtml.trim()) {
      setError("Please get the MailerSend template HTML before inserting events.");
      return;
    }

    const eventAreaIndex = currentHtml.indexOf(EVENT_AREA_MARKER);
    const secondaryCtaIndex = currentHtml.indexOf(SECONDARY_CTA_MARKER);

    if (eventAreaIndex === -1 || secondaryCtaIndex === -1 || secondaryCtaIndex <= eventAreaIndex) {
      setError("Could not find Event Area and Secondary CTA markers in the template HTML.");
      return;
    }

    const beforeEventCards = currentHtml.slice(
      0,
      eventAreaIndex + EVENT_AREA_MARKER.length
    );
    const afterEventCards = currentHtml.slice(secondaryCtaIndex);
    const nextHtml = `${beforeEventCards}
      <!-- Featured event cards generated from live approved upcoming events -->
${buildSelectedEventCardsHtml()}

      ${afterEventCards}`;

    setHtmlContent(nextHtml);
    setSuccess(`${selectedEventIds.length} featured event card(s) inserted into Event Area.`);
  };

  const fetchMailerSendTemplate = async () => {
    try {
      setFetching(true);
      setError("");
      setSuccess("");
      const headers = await getMailerSendAuthHeaders();

      const response = await fetch(
        `${MAILERSEND_API_BASE}/templates/${WEEKLY_TEMPLATE_ID}`,
        {
          method: "GET",
          headers,
        }
      );

      const data = await parseMailerSendResponse(response);
      if (!response.ok) {
        throw new Error(data?.message || `MailerSend request failed (${response.status})`);
      }

      const template = getTemplatePayload(data);
      const nextHtml = getTemplateHtml(template);
      setTemplateMeta(template);
      setTemplateName(template.name || templateName || "Weekly Email Campaign");
      setHtmlContent(nextHtml);
      setTextContent(getTemplateText(template));

      if (nextHtml) {
        setSuccess("MailerSend template loaded.");
      } else {
        setError("Template loaded, but no HTML field was returned by MailerSend.");
      }
    } catch (err) {
      setError("Failed to load MailerSend template: " + err.message);
    } finally {
      setFetching(false);
    }
  };

  const updateMailerSendTemplate = async () => {
    if (!htmlContent.trim()) {
      setError("Please enter template HTML.");
      return;
    }

    try {
      setUpdatingMailerSend(true);
      setError("");
      setSuccess("");
      const headers = await getMailerSendAuthHeaders();

      const normalizedHtmlContent = normalizeResponsiveEventCardsHtml(htmlContent);

      const response = await fetch(
        `${MAILERSEND_API_BASE}/templates/${WEEKLY_TEMPLATE_ID}`,
        {
          method: "PUT",
          headers,
          body: JSON.stringify({
            name: templateName.trim() || "Weekly Email Campaign",
            html: normalizedHtmlContent,
            text: textContent || normalizedHtmlContent.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim(),
            auto_generate: !textContent.trim(),
          }),
        }
      );

      const data = await parseMailerSendResponse(response).catch(() => null);
      if (!response.ok) {
        throw new Error(data?.message || `MailerSend update failed (${response.status})`);
      }

      setTemplateMeta(getTemplatePayload(data));
      setHtmlContent(normalizedHtmlContent);
      setSuccess("MailerSend template updated.");
    } catch (err) {
      setError("Failed to update MailerSend template: " + err.message);
    } finally {
      setUpdatingMailerSend(false);
    }
  };

  const editingEvent = featuredEvents.find((event) => event.id === editingEventId);

  return (
    <Container fluid className="py-4">
      <Row className="mb-4">
        <Col>
          <Button
            variant="outline-secondary"
            onClick={() => navigate("/admin-weekly-email-campaign")}
            className="mb-3"
          >
            <FaArrowLeft className="me-2" />
            Back to Weekly Campaign
          </Button>
          <h2 className="mb-1">
            <FaCode className="me-2" />
            MailerSend Template Editor
          </h2>
          <p className="text-muted mb-0">
            Load and modify MailerSend template {WEEKLY_TEMPLATE_ID}.
          </p>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}

      <Row className="g-4">
        <Col lg={4}>
          <Card className="border-0 shadow-sm mb-4">
            <Card.Header className="bg-primary text-white">
              <h5 className="mb-0">
                <FaCloudDownloadAlt className="me-2" />
                Template Actions
              </h5>
            </Card.Header>
            <Card.Body>
              <div className="mb-3">
                <div className="small text-muted">Template ID</div>
                <code>{WEEKLY_TEMPLATE_ID}</code>
              </div>

              <Alert
                variant={
                  usesLocalDevMailerSendProxy()
                    ? getLocalMailerSendToken()
                      ? "light"
                      : "warning"
                    : "light"
                }
                className="border"
              >
                {usesLocalDevMailerSendProxy() ? (
                  <>
                    Local dev proxy uses <code>REACT_APP_MS_TOKEN</code>.
                  </>
                ) : (
                  "Production uses the Firebase mailersendApi function with your signed-in admin session."
                )}
              </Alert>

              <div className="d-grid gap-2">
                <Button
                  variant="primary"
                  onClick={fetchMailerSendTemplate}
                  disabled={fetching}
                >
                  {fetching ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Loading...
                    </>
                  ) : (
                    "Get Template HTML"
                  )}
                </Button>
                <Button
                  variant="outline-secondary"
                  onClick={loadCampaignHtmlTemplate}
                  disabled={fetching}
                >
                  Reload campaign.html
                </Button>
                <Button
                  variant="outline-primary"
                  onClick={updateMailerSendTemplate}
                  disabled={updatingMailerSend || !htmlContent.trim()}
                >
                  {updatingMailerSend ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Updating...
                    </>
                  ) : (
                    "Update MailerSend Template"
                  )}
                </Button>
              </div>
            </Card.Body>
          </Card>

          {templateMeta?.updated_at && (
            <small className="text-muted">MailerSend updated {templateMeta.updated_at}</small>
          )}
        </Col>

        <Col lg={8}>
          <Card
            className="border-0 shadow-sm mb-4"
            style={
              isEventEditorFullScreen
                ? {
                    position: "fixed",
                    inset: "16px",
                    zIndex: 1060,
                    overflow: "hidden",
                  }
                : undefined
            }
          >
            <Card.Header className="bg-success text-white">
              <div className="d-flex align-items-center justify-content-between gap-2">
                <h5 className="mb-0">Live Upcoming Event Selection</h5>
                <Button
                  variant="outline-light"
                  size="sm"
                  onClick={() => setIsEventEditorFullScreen((current) => !current)}
                  title={isEventEditorFullScreen ? "Exit full screen" : "Open full screen"}
                  aria-label={isEventEditorFullScreen ? "Exit full screen" : "Open full screen"}
                >
                  {isEventEditorFullScreen ? <FaCompress /> : <FaExpand />}
                </Button>
              </div>
            </Card.Header>
            <Card.Body
              style={
                isEventEditorFullScreen
                  ? {
                      height: "calc(100vh - 96px)",
                      overflowY: "auto",
                    }
                  : undefined
              }
            >
              <div className="d-flex gap-2 mb-3">
                <Button
                  variant="outline-success"
                  size="sm"
                  onClick={loadFeaturedEvents}
                  disabled={loadingFeaturedEvents}
                >
                  {loadingFeaturedEvents ? "Loading..." : "Refresh"}
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => {
                    setSelectedEventIds(featuredEvents.map((event) => event.id));
                    setEventImageCrops((crops) => {
                      const nextCrops = { ...crops };
                      featuredEvents.forEach((event) => {
                        nextCrops[event.id] = nextCrops[event.id] || getDefaultEventImageCrop();
                      });
                      return nextCrops;
                    });
                  }}
                  disabled={loadingFeaturedEvents || featuredEvents.length === 0}
                >
                  Select All
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => setSelectedEventIds([])}
                  disabled={selectedEventIds.length === 0}
                >
                  Clear
                </Button>
              </div>

              {loadingFeaturedEvents ? (
                <div className="text-center py-3">
                  <Spinner animation="border" size="sm" />
                  <p className="text-muted mt-2 mb-0">Loading live upcoming events...</p>
                </div>
              ) : featuredEvents.length === 0 ? (
                <Alert variant="info" className="py-2 mb-3">
                  No live approved upcoming events found.
                </Alert>
              ) : (
                <div
                  className="border rounded mb-3"
                  style={{
                    maxHeight: isEventEditorFullScreen ? "42vh" : "520px",
                    overflowY: "auto",
                  }}
                >
                  {featuredEvents.map((event) => (
                    <div
                      key={event.id}
                      className="d-flex gap-3 align-items-start p-3 border-bottom"
                    >
                      <Form.Check
                        type="checkbox"
                        checked={selectedEventIds.includes(event.id)}
                        onChange={() => toggleSelectedEvent(event.id)}
                        className="mt-1"
                      />
                      <img
                        src={getEventImageSrc(event)}
                        alt={event.title || "Featured event"}
                        style={{
                          width: "92px",
                          height: "92px",
                          objectFit: "cover",
                          borderRadius: "8px",
                          flex: "0 0 92px",
                          backgroundColor: "#f4f4f0",
                        }}
                      />
                      <div className="flex-grow-1">
                        <div className="fw-bold">{event.title || "Untitled featured event"}</div>
                        <div
                          className="small text-muted"
                          style={{
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                        >
                          {getEventDescription(event)}
                        </div>
                        <div className="small text-muted mt-1">
                          {[formatEventDate(event), getEventLocation(event)].filter(Boolean).join(" | ")}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {selectedEventIds.length > 0 ? (
                <div className="mb-3">
                  <div className="small fw-bold mb-2">Selected order</div>
                  <div
                    className="border rounded"
                    style={{
                      maxHeight: isEventEditorFullScreen ? "none" : "620px",
                      overflowY: isEventEditorFullScreen ? "visible" : "auto",
                    }}
                  >
                    {getSelectedEvents().map((event, index) => {
                      const crop = getEventImageCrop(event.id);
                      const canRepositionImage = crop.fit === "cover" && crop.fillFrame;
                      const previewPositionX = canRepositionImage ? crop.positionX : 50;
                      const previewPositionY = canRepositionImage ? crop.positionY : 50;
                      const cropModeLabel = crop.fit === "contain" ? "full image" : "filled image";
                      const previewMaxWidth = crop.fillFrame
                        ? "100%"
                        : isEventEditorFullScreen
                          ? "760px"
                          : "560px";

                      return (
                        <div
                          key={event.id}
                          className="p-3 border-bottom"
                        >
                          <div className="d-flex align-items-center gap-2 mb-3">
                            <div
                              className="text-muted small"
                              style={{ width: "28px", flex: "0 0 28px" }}
                            >
                              {index + 1}
                            </div>
                            <div className="flex-grow-1">
                              <div className="fw-bold">{event.title || "Untitled event"}</div>
                              <div className="small text-muted">
                                {formatEventDate(event) || "Date TBA"}
                              </div>
                            </div>
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => moveSelectedEvent(event.id, -1)}
                              disabled={index === 0}
                              title="Move up"
                              aria-label={`Move ${event.title || "event"} up`}
                            >
                              <FaArrowUp />
                            </Button>
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => moveSelectedEvent(event.id, 1)}
                              disabled={index === selectedEventIds.length - 1}
                              title="Move down"
                              aria-label={`Move ${event.title || "event"} down`}
                            >
                              <FaArrowDown />
                            </Button>
                          </div>

                          <Row className="g-3 align-items-start">
                            <Col md={7}>
                              <div
                                className="border rounded overflow-hidden bg-light"
                                onPointerDown={(e) => {
                                  if (canRepositionImage) {
                                    handleFillImagePointer(event.id, e);
                                  }
                                }}
                                onPointerMove={(e) => {
                                  if (canRepositionImage && e.buttons === 1) {
                                    handleFillImagePointer(event.id, e);
                                  }
                                }}
                                style={{
                                  width: "100%",
                                  maxWidth: previewMaxWidth,
                                  aspectRatio: `${crop.width} / ${crop.height}`,
                                  margin: "0 auto",
                                  cursor: canRepositionImage ? "crosshair" : "default",
                                }}
                              >
                                <img
                                  src={getEventImageSrc(event)}
                                  alt={event.title || "Event crop preview"}
                                  draggable={false}
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: crop.fit,
                                    objectPosition: `${previewPositionX}% ${previewPositionY}%`,
                                    transform: `scale(${crop.fit === "contain" ? 1 : crop.zoom / 100})`,
                                    transformOrigin: `${previewPositionX}% ${previewPositionY}%`,
                                    display: "block",
                                  }}
                                />
                              </div>
                              <div className="small text-muted mt-2">
                                {crop.width} x {crop.height}, {cropModeLabel}
                                {crop.fit === "cover" ? `, zoom ${crop.zoom}%` : ""}
                                {canRepositionImage
                                  ? `, focus ${previewPositionX}% ${previewPositionY}%`
                                  : ", centered"}
                              </div>
                            </Col>

                            <Col md={5}>
                              <div className="d-flex gap-2 flex-wrap mb-3">
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() => openImageEditor(event)}
                                >
                                  <FaCrop className="me-1" />
                                  Edit photo
                                </Button>
                                <Button
                                  variant="outline-secondary"
                                  size="sm"
                                  onClick={() => downloadEventImage(event)}
                                >
                                  <FaCloudDownloadAlt className="me-1" />
                                  Download image
                                </Button>
                                <Button
                                  variant={crop.fit === "contain" ? "secondary" : "outline-secondary"}
                                  size="sm"
                                  onClick={() => applyFullImageCrop(event)}
                                >
                                  Full image
                                </Button>
                                <Button
                                  variant={
                                    crop.fit === "cover" &&
                                    crop.fillFrame
                                      ? "secondary"
                                      : "outline-secondary"
                                  }
                                  size="sm"
                                  onClick={() => applyFillImageCrop(event)}
                                >
                                  Fill image
                                </Button>
                              </div>

                              <Form.Group className="mb-3">
                                <Form.Label className="small mb-1">Image zoom</Form.Label>
                                <Form.Range
                                  min={MIN_IMAGE_ZOOM}
                                  max={MAX_IMAGE_ZOOM}
                                  value={crop.zoom}
                                  disabled={crop.fit === "contain"}
                                  onChange={(e) =>
                                    updateEventImageCrop(event.id, {
                                      fit: "cover",
                                      fillFrame: crop.fillFrame,
                                      zoom: e.target.value,
                                    })
                                  }
                                />
                              </Form.Group>

                              {canRepositionImage ? (
                                <>
                                  <Form.Group className="mb-3">
                                    <Form.Label className="small mb-1">Horizontal position</Form.Label>
                                    <Form.Range
                                      min="0"
                                      max="100"
                                      value={crop.positionX}
                                      onChange={(e) =>
                                        updateEventImageCrop(event.id, {
                                          fit: "cover",
                                          fillFrame: true,
                                          positionX: e.target.value,
                                        })
                                      }
                                    />
                                  </Form.Group>

                                  <Form.Group className="mb-0">
                                    <Form.Label className="small mb-1">Vertical position</Form.Label>
                                    <Form.Range
                                      min="0"
                                      max="100"
                                      value={crop.positionY}
                                      onChange={(e) =>
                                        updateEventImageCrop(event.id, {
                                          fit: "cover",
                                          fillFrame: true,
                                          positionY: e.target.value,
                                        })
                                      }
                                    />
                                  </Form.Group>
                                </>
                              ) : null}

                            </Col>
                          </Row>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : null}

              <Button
                variant="success"
                onClick={insertSelectedEvents}
                disabled={selectedEventIds.length === 0}
                className="w-100"
              >
                Insert Selected Events Into HTML
              </Button>
              <small className="text-muted d-block mt-2">
                Replaces the event cards between <code>{EVENT_AREA_MARKER}</code> and{" "}
                <code>{SECONDARY_CTA_MARKER}</code>.
              </small>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={12}>
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-info text-white">
              <h5 className="mb-0">
                <FaEye className="me-2" />
                HTML Editor
              </h5>
            </Card.Header>
            <Card.Body>
              <Tabs defaultActiveKey="render" className="mb-3">
                <Tab eventKey="render" title="Rendered HTML">
                  <div className="border rounded bg-white" style={{ height: "720px", overflow: "hidden" }}>
                    <iframe
                      title="Rendered email template"
                      srcDoc={htmlContent || "<p style='font-family: Arial; padding: 16px;'>No HTML loaded.</p>"}
                      sandbox=""
                      style={{
                        width: "100%",
                        height: "100%",
                        border: 0,
                        backgroundColor: "#fff",
                      }}
                    />
                  </div>
                </Tab>
                <Tab eventKey="mobile" title="Mobile Preview">
                  <div
                    className="border rounded bg-light p-3"
                    style={{ height: "760px", overflow: "auto" }}
                  >
                    <div className="d-flex justify-content-center align-items-center gap-2 mb-3">
                      <Form.Label className="small text-muted mb-0" htmlFor="mobile-preview-width">
                        Mobile width
                      </Form.Label>
                      <Form.Select
                        id="mobile-preview-width"
                        size="sm"
                        value={mobilePreviewWidth}
                        onChange={(e) => setMobilePreviewWidth(Number(e.target.value))}
                        style={{ width: "150px" }}
                      >
                        <option value={320}>320px</option>
                        <option value={360}>360px</option>
                        <option value={375}>375px</option>
                        <option value={390}>390px</option>
                        <option value={414}>414px</option>
                        <option value={430}>430px</option>
                      </Form.Select>
                    </div>
                    <div
                      className="mx-auto"
                      style={{ width: `${mobilePreviewWidth}px`, maxWidth: "100%" }}
                    >
                      <div className="small text-muted text-center mb-2">
                        {mobilePreviewWidth}px mobile viewport
                      </div>
                      <iframe
                        title="Mobile email template preview"
                        srcDoc={htmlContent || "<p style='font-family: Arial; padding: 16px;'>No HTML loaded.</p>"}
                        sandbox=""
                        style={{
                          display: "block",
                          width: `${mobilePreviewWidth}px`,
                          maxWidth: "100%",
                          height: "720px",
                          border: "1px solid #dee2e6",
                          borderRadius: "18px",
                          backgroundColor: "#fff",
                        }}
                      />
                    </div>
                  </div>
                </Tab>
                <Tab eventKey="html" title="HTML Source">
                  <Form.Control
                    as="textarea"
                    rows={28}
                    value={htmlContent}
                    onChange={(e) => setHtmlContent(e.target.value)}
                    placeholder="Paste or fetch MailerSend template HTML"
                    style={{ fontFamily: "monospace", fontSize: "0.9rem" }}
                  />
                </Tab>
              </Tabs>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Modal
        show={showImageEditorModal}
        onHide={() => closeImageEditor()}
        size="lg"
        centered
        backdrop={uploadingEditedImage ? "static" : true}
      >
        <Modal.Header closeButton={!uploadingEditedImage}>
          <Modal.Title>Edit event photo</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <div className="fw-bold">{editingEvent?.title || "Selected event"}</div>
            <div className="small text-muted">
              Crop export is locked to {CROP_IMAGE_WIDTH} x {CROP_IMAGE_HEIGHT}.
            </div>
          </div>

          {imageEditorError ? (
            <Alert variant="danger" className="py-2">
              {imageEditorError}
            </Alert>
          ) : null}

          <Form.Group className="mb-3">
            <Form.Label className="small mb-1">Use another source image optional</Form.Label>
            <Form.Control
              type="file"
              accept="image/*"
              onChange={handleEditorImageFileChange}
              disabled={uploadingEditedImage}
            />
          </Form.Group>

          <div
            className="border rounded bg-dark"
            style={{
              position: "relative",
              height: "420px",
              overflow: "hidden",
            }}
          >
            {editorImageSrc ? (
              <Cropper
                image={editorImageSrc}
                crop={editorCrop}
                zoom={editorZoom}
                aspect={CROP_IMAGE_ASPECT}
                cropSize={{
                  width: CROP_PREVIEW_WIDTH,
                  height: CROP_PREVIEW_HEIGHT,
                }}
                onCropChange={setEditorCrop}
                onZoomChange={setEditorZoom}
                onCropComplete={(_, croppedAreaPixels) =>
                  setEditorCroppedAreaPixels(croppedAreaPixels)
                }
                onMediaLoaded={handleEditorImageLoaded}
                showGrid={false}
                objectFit="horizontal-cover"
              />
            ) : (
              <div className="h-100 d-flex align-items-center justify-content-center text-white">
                No image selected.
              </div>
            )}
          </div>

          <Form.Group className="mt-3">
            <Form.Label className="small mb-1">Zoom</Form.Label>
            <Form.Range
              min={1}
              max={3}
              step={0.01}
              value={editorZoom}
              onChange={(event) => setEditorZoom(Number(event.target.value))}
              disabled={uploadingEditedImage}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => closeImageEditor()}
            disabled={uploadingEditedImage}
          >
            Cancel
          </Button>
          <Button
            variant="outline-primary"
            onClick={uploadSelectedImageWithoutEditing}
            disabled={uploadingEditedImage || !editorSelectedFile}
          >
            {uploadingEditedImage ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Uploading
              </>
            ) : (
              <>
                <FaUpload className="me-2" />
                Upload without editing
              </>
            )}
          </Button>
          <Button
            variant="primary"
            onClick={saveEditedEventImage}
            disabled={uploadingEditedImage || !editorImageSrc}
          >
            {uploadingEditedImage ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Uploading
              </>
            ) : (
              <>
                <FaUpload className="me-2" />
                Save and upload
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
